import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ENVIRONMENT,
  MFAVerifyResponse,
  PostLoginMFAResponse,
  SendLoginMFACodeRequest,
  SendMFARequest,
  SuccessfulLoginNoteRequest,
  UserProfile,
  VerificationType,
  VerifyLoginMFACodeRequest,
  VerifyMFARequest,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  private userProfileCache: { profile: UserProfile; timestamp: number; userId: string } | null = null;

  private userProfileRequest: Promise<UserProfile> | null = null;

  private readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private get baseUrl(): string {
    return this.env.appSettings.profileDomainUrl || '';
  }

  private get apiNamespace(): string {
    return this.env.appSettings.apiNamespace || 'api/v1';
  }

  async postLoginMfa(userId: string): Promise<PostLoginMFAResponse> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile/ProcessLoginMFA/${userId}`;

    const response = await firstValueFrom(this.http.post(url, {}, { responseType: 'text', withCredentials: true }));

    if (!response || (typeof response === 'string' && response.trim() === '')) {
      return {} as PostLoginMFAResponse;
    }

    if (typeof response === 'object') {
      return response as unknown as PostLoginMFAResponse;
    }

    try {
      return JSON.parse(response) as PostLoginMFAResponse;
    } catch (error) {
      this.loggingService.logException(error, '[ProfileService] ProcessLoginMFA returned non-JSON response');
      throw new Error(`ProcessLoginMFA error: ${response.trim()}`);
    }
  }

  async sendLoginMfaCode(
    userId: string,
    contactId: string,
    verificationType: VerificationType,
    rememberMe = false,
    firstName?: string,
  ): Promise<void> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile/SendLoginMFACode/${userId}`;

    const request: SendLoginMFACodeRequest = {
      globalUserId: userId,
      key: contactId,
      verificationType,
      rememberMe,
      firstName,
    };

    await firstValueFrom(this.http.post(url, request));
  }

  async verifyLoginMfaCode(
    userId: string,
    contactId: string,
    verificationType: VerificationType,
    code: string,
    rememberMe = false,
    firstName?: string,
  ): Promise<MFAVerifyResponse> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile/VerifyLoginMFACode/${userId}`;

    const request: VerifyLoginMFACodeRequest = {
      globalUserId: userId,
      key: contactId,
      verificationType,
      rememberMe,
      code,
      firstName,
    };

    return firstValueFrom(this.http.post<MFAVerifyResponse>(url, request, { withCredentials: true }));
  }

  async getUserProfile(userId: string): Promise<UserProfile> {
    if (this.userProfileCache && this.userProfileCache.userId === userId) {
      const age = Date.now() - this.userProfileCache.timestamp;
      if (age < this.CACHE_TTL_MS) {
        return this.userProfileCache.profile;
      }
    }

    if (this.userProfileRequest) {
      return this.userProfileRequest;
    }

    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile/${userId}`;

    this.userProfileRequest = firstValueFrom(this.http.get<UserProfile>(url))
      .then((profile) => {
        this.userProfileCache = { profile, timestamp: Date.now(), userId };
        this.userProfileRequest = null;
        return profile;
      })
      .catch((error) => {
        this.userProfileRequest = null;
        throw error;
      });

    return this.userProfileRequest;
  }

  clearUserProfileCache(): void {
    this.userProfileCache = null;
    this.userProfileRequest = null;
  }

  async registerUser(profile: {
    globalUserID: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  }): Promise<boolean> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/UserProfile`;
    return firstValueFrom(this.http.post<boolean>(url, profile));
  }

  shouldShowContactReview(userProfile: UserProfile): boolean {
    if (!userProfile.showContactReview) {
      return false;
    }

    const alreadyShown = localStorage.getItem('ContactInfoReviewShown');
    if (alreadyShown === 'true') {
      return false;
    }

    return true;
  }

  markContactReviewDisplayed(): void {
    localStorage.setItem('ContactInfoReviewShown', 'true');
  }

  markContactReviewConfirmed(): void {
    localStorage.setItem('ContactInfoReviewConfirmed', 'true');
  }

  async sendMfaCode(
    loanNumber: string,
    contactValue: string,
    verificationType: VerificationType,
    firstName: string,
    key: string,
  ): Promise<void> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/Mfa/SendMFACode/${loanNumber}`;

    const request: SendMFARequest = {
      loanNumber,
      verificationType,
      firstName,
      key,
    };

    await firstValueFrom(this.http.post(url, request));
  }

  async verifyMfaCode(
    loanNumber: string,
    contactValue: string,
    verificationType: VerificationType,
    verificationCode: string,
    firstName: string,
    key: string,
  ): Promise<MFAVerifyResponse> {
    const url = `${this.baseUrl}/${this.apiNamespace}/LDProfileDomain/Mfa/VerifyMFACode/${loanNumber}`;

    const request: VerifyMFARequest = {
      loanNumber,
      verificationType,
      firstName,
      key,
      code: verificationCode,
    };

    return firstValueFrom(this.http.post<MFAVerifyResponse>(url, request));
  }

  async successfulLoginNote(payload: SuccessfulLoginNoteRequest): Promise<string> {
    const url = this.env.appSettings.successfulLoginNoteUrl;
    if (!url) {
      this.loggingService.logWarning('[ProfileService] successfulLoginNoteUrl not configured');
      return '';
    }

    const response = await firstValueFrom(this.http.post(url, payload, { responseType: 'text' }));
    return response;
  }
}
